import { useEffect, useMemo, useState } from 'react';
import { Check, Eye, Loader2, MoveRight, Send, Square, X } from 'lucide-react';

import type { TtqModelInvocationOptions } from '@kelilearning/psycheflow-contracts';

import { formatTtqModelAuditDisplay } from '../../utils/ttqModelBrand';
import { TtqAngleRelationIcon } from './TtqAngleRelationIcon';
import {
  TTQ_BOTTOM_PANEL_L1_WIDTH_PX,
  TTQ_BOTTOM_PANEL_SHELL_CLASS,
  TTQ_BOTTOM_PANEL_SHELL_STYLE,
} from './ttqBottomPanelShell';
import { formatTtqWorkbenchError } from './ttqWorkbenchErrors';
import { TtqWorkbenchModelMenu } from './TtqWorkbenchModelMenu';
import {
  getTtqWorkbenchModelOption,
  readStoredTtqWorkbenchModelId,
  toTtqModelInvocation,
} from './ttqWorkbenchModels';

export type TtqCttqDirectionPreview = {
  directionPreview: string;
  modelAudit?: { provider?: string; model?: string; mode?: string } | null;
};

type TtqBottomPanelCttqProps = {
  visible: boolean;
  memberParticleIds: string[];
  memberLabels?: Record<string, string>;
  submitting?: boolean;
  error?: string | null;
  errorDetail?: string | null;
  onClose: () => void;
  onPreviewDirection: (payload: {
    cttqParticleId: string;
    memberParticleIds: string[];
    description: string;
    model?: TtqModelInvocationOptions;
  }) => Promise<TtqCttqDirectionPreview>;
  onSubmit: (payload: {
    cttqParticleId: string;
    memberParticleIds: string[];
    description: string;
    directionPreview?: string;
    model?: TtqModelInvocationOptions;
  }) => void;
};

const L1_DESCRIPTION = '将选中成员题收敛为一道代表知识颗粒（CTTQ），由 ttq-math-cttq 生成新颗粒并写入收敛记录。';

function buildLocalPromptPreview(input: {
  cttqLabel: string;
  memberLabels: string[];
  customPrompt: string;
}) {
  return [
    '【任务】收敛品题（CTTQ · 预览稿）',
    '【策略】以指定代表题种子为锚，综合其余成员题，提取可覆盖复习的代表知识颗粒。',
    `【代表题种子】${input.cttqLabel}`,
    '【参与颗粒】',
    ...input.memberLabels.map((label, index) => `${index + 1}. ${label}`),
    input.customPrompt ? `【补充说明】${input.customPrompt}` : '',
    '【产出】生成代表知识颗粒，并创建收敛记录。',
  ].filter(Boolean).join('\n');
}

/** 鸟瞰 · 收敛品题底栏（仅当前选中颗粒） */
export function TtqBottomPanelCttq({
  visible,
  memberParticleIds,
  memberLabels = {},
  submitting = false,
  error = null,
  errorDetail = null,
  onClose,
  onPreviewDirection,
  onSubmit,
}: TtqBottomPanelCttqProps) {
  const [description, setDescription] = useState('');
  const [cttqParticleId, setCttqParticleId] = useState<string | null>(null);
  const [directionPreview, setDirectionPreview] = useState<TtqCttqDirectionPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewErrorDetail, setPreviewErrorDetail] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [modelId, setModelId] = useState(() => readStoredTtqWorkbenchModelId());
  const selectedModel = useMemo(
    () => toTtqModelInvocation(getTtqWorkbenchModelOption(modelId)),
    [modelId],
  );

  useEffect(() => {
    if (!visible) {
      setDescription('');
      setCttqParticleId(null);
      setDirectionPreview(null);
      setPreviewError(null);
      setPreviewErrorDetail(null);
      setDetailOpen(false);
      setShowPrompt(false);
      return;
    }
    setModelId(readStoredTtqWorkbenchModelId());
    setCttqParticleId((current) => (
      current && memberParticleIds.includes(current)
        ? current
        : memberParticleIds[0] ?? null
    ));
  }, [memberParticleIds, visible]);

  const labelOf = (id: string) => {
    const label = String(memberLabels[id] || '').trim();
    return label || id.slice(0, 8);
  };

  const promptPreviewText = useMemo(() => {
    if (!cttqParticleId) return '';
    return buildLocalPromptPreview({
      cttqLabel: labelOf(cttqParticleId),
      memberLabels: memberParticleIds.map(labelOf),
      customPrompt: description.trim(),
    });
  }, [cttqParticleId, description, memberLabels, memberParticleIds]);

  const previewModel = formatTtqModelAuditDisplay(directionPreview?.modelAudit);

  if (!visible || memberParticleIds.length < 2 || !cttqParticleId) return null;

  const busy = submitting || previewing;
  const hasDirectionPreview = Boolean(directionPreview?.directionPreview?.trim());
  const canSubmit = !busy && hasDirectionPreview;

  return (
    <div className={TTQ_BOTTOM_PANEL_SHELL_CLASS} style={TTQ_BOTTOM_PANEL_SHELL_STYLE}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-ink">收敛品题</span>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {memberParticleIds.length} 题
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            setDescription('');
            setDirectionPreview(null);
            onClose();
          }}
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted hover:bg-black/[0.05]"
        >
          <X size={14} />
          关闭
        </button>
      </div>

      <div className="overflow-x-auto overscroll-x-contain px-3 py-2 [-webkit-overflow-scrolling:touch]">
        <div className="flex w-full max-w-full flex-nowrap items-stretch justify-start gap-1.5">
          <div
            className="flex shrink-0 cursor-not-allowed flex-col items-start px-1 py-1 text-left text-muted"
            style={{ width: TTQ_BOTTOM_PANEL_L1_WIDTH_PX }}
            aria-disabled
          >
            <div className="flex w-full items-start justify-between gap-2">
              <TtqAngleRelationIcon angleId="cttq_converge" size="sm" muted />
              <span className="shrink-0 text-[10px] font-medium text-muted">多对一</span>
            </div>
            <p className="mt-1 line-clamp-3 text-xs text-muted">{L1_DESCRIPTION}</p>
            <span className="mt-1 text-[10px] text-muted">生成型 · ttq-math-cttq</span>
          </div>
        </div>
      </div>

      <div className="max-h-28 space-y-1 overflow-y-auto border-t border-border px-4 py-2">
        <p className="text-[11px] font-medium text-muted">选择代表题种子（其余为被收敛 member）</p>
        {memberParticleIds.map((id, index) => {
          const selected = cttqParticleId === id;
          return (
            <button
              key={id}
              type="button"
              disabled={busy}
              onClick={() => {
                setCttqParticleId(id);
                setDirectionPreview(null);
                setPreviewError(null);
              }}
              className={
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs '
                + (selected
                  ? 'bg-primary/10 text-primary'
                  : 'text-ink hover:bg-black/[0.04]')
              }
            >
              <span className="w-4 shrink-0 font-medium text-ink/50">{index + 1}.</span>
              <span className="min-w-0 flex-1 truncate">{labelOf(id)}</span>
              {selected ? <span className="shrink-0 text-[10px]">种子</span> : null}
            </button>
          );
        })}
      </div>

      <div className="border-t border-border px-4 py-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <label className="text-[11px] font-medium text-muted">补充说明（可选）</label>
          <button
            type="button"
            disabled={busy || !promptPreviewText}
            onClick={() => setShowPrompt((open) => !open)}
            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-primary hover:bg-primary/5 disabled:opacity-40"
          >
            <Eye size={12} />
            {showPrompt ? '收起提示词' : '提示词预览'}
          </button>
        </div>
        {showPrompt && promptPreviewText ? (
          <pre className="mb-2 max-h-28 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-canvas/60 px-3 py-2 text-[11px] leading-relaxed text-ink/80">
            {promptPreviewText}
          </pre>
        ) : null}
        <textarea
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
            setDirectionPreview(null);
          }}
          rows={2}
          placeholder="希望如何收敛、代表题应突出什么…"
          className="w-full resize-none rounded-lg border border-border bg-canvas/60 px-3 py-2 text-sm text-ink outline-none focus:border-primary/40"
          disabled={busy}
        />

        <div className="mt-2">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium text-muted">聚合生成提示词预览</p>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPreviewError(null);
                setPreviewErrorDetail(null);
                setPreviewing(true);
                void onPreviewDirection({
                  cttqParticleId,
                  memberParticleIds,
                  description: description.trim(),
                  model: selectedModel,
                }).then((result) => {
                  setDirectionPreview(result);
                }).catch((previewFail) => {
                  setDirectionPreview(null);
                  const formatted = formatTtqWorkbenchError(previewFail);
                  setPreviewError(formatted.summary);
                  setPreviewErrorDetail(formatted.detail);
                }).finally(() => {
                  setPreviewing(false);
                });
              }}
              className={
                'inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium '
                + (busy
                  ? 'cursor-not-allowed text-muted'
                  : 'text-ink hover:bg-black/[0.04]')
              }
            >
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              聚合生成提示词预览
            </button>
          </div>
          {directionPreview ? (
            <div className="rounded-lg border border-border bg-canvas/40 px-3 py-2 text-[11px] text-ink/80">
              <p className="whitespace-pre-wrap leading-relaxed">{directionPreview.directionPreview}</p>
              {previewModel ? (
                <p className="mt-2 flex items-center gap-1.5 text-[10px] text-muted">
                  预览模型：
                  {previewModel.iconUrl ? (
                    <img src={previewModel.iconUrl} alt="" className="h-3.5 w-3.5 rounded-sm" />
                  ) : null}
                  <span className="font-medium text-ink/70">{previewModel.label}</span>
                </p>
              ) : null}
              <p className="mt-1 text-[10px] text-muted">已纳入后续生成 · 修改说明后需重新预览</p>
            </div>
          ) : (
            <p className="text-[11px] font-medium text-amber-800">
              须先点「聚合生成提示词预览」后才能生成代表题
            </p>
          )}
        </div>

        {previewError || error ? (
          <div className="mt-1.5 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
            <p className="text-xs leading-relaxed text-danger">{previewError || error}</p>
            {(previewErrorDetail || errorDetail) ? (
              <button
                type="button"
                className="mt-1.5 inline-flex items-center gap-1 rounded border border-danger/35 px-2 py-0.5 text-[11px] font-medium text-danger hover:bg-danger/10"
                onClick={() => setDetailOpen(true)}
              >
                查看错误详情
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted">
            <span className="font-medium text-ink/70">图例</span>
            <span className="inline-flex items-center gap-1"><Square size={12} />题</span>
            <span className="inline-flex items-center gap-1"><Check size={12} />解</span>
            <span className="inline-flex items-center gap-1"><MoveRight size={12} />收敛</span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <TtqWorkbenchModelMenu
              value={modelId}
              disabled={busy}
              onChange={(option) => setModelId(option.id)}
            />
            <button
              type="button"
              disabled={!canSubmit}
              title={!hasDirectionPreview ? '请先完成聚合生成提示词预览' : undefined}
              onClick={() => {
                if (!hasDirectionPreview) return;
                onSubmit({
                  cttqParticleId,
                  memberParticleIds,
                  description: description.trim(),
                  directionPreview: directionPreview?.directionPreview?.trim() || undefined,
                  model: selectedModel,
                });
              }}
              className={
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium '
                + (submitting || canSubmit
                  ? 'bg-primary text-white hover:bg-primary/90 disabled:opacity-90'
                  : 'cursor-not-allowed bg-black/[0.04] text-muted')
              }
            >
              {submitting ? <Loader2 size={14} className="animate-spin text-white" /> : <Send size={14} />}
              {submitting ? '生成中…' : '生成代表题'}
            </button>
          </div>
        </div>
      </div>

      {detailOpen && (previewErrorDetail || errorDetail) ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 p-4">
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-sm font-semibold text-ink">错误详情</p>
              <button type="button" className="text-muted hover:text-ink" onClick={() => setDetailOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap px-4 py-3 text-[11px] leading-relaxed text-ink">
              {previewErrorDetail || errorDetail}
            </pre>
            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 text-xs text-muted hover:bg-black/[0.03]"
                onClick={() => setDetailOpen(false)}
              >
                关闭
              </button>
              <button
                type="button"
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white"
                onClick={() => {
                  void navigator.clipboard?.writeText(String(previewErrorDetail || errorDetail || ''));
                }}
              >
                复制详情
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
